import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { richTextToPlain } from "@somasi/shared";
import { useConfirmDelete } from "@/components/confirm-dialog";
import { api } from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, FileDown, Save, Send, Trash2 } from "lucide-react";
import { notify } from "@/lib/notify";
import { useAuth } from "@/lib/auth";
import { money } from "@/lib/billing";
import { invalidateInvoiceStamps } from "@/lib/invoices";
import { POSTAL_ADDRESS_COLUMNS } from "@somasi/shared";
import {
  getInvoiceDownloadUrl,
  issueInvoice,
  requestInvoiceArchive,
} from "@/integrations/aws/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/invoices/$invoiceId")({
  head: () => ({
    meta: [
      { title: "Invoice — Somasi Ops" },
      {
        name: "description",
        content: "Review, edit and download a Somasi client invoice as a branded PDF.",
      },
      { property: "og:title", content: "Invoice — Somasi Ops" },
      {
        property: "og:description",
        content: "Review, edit and download a Somasi client invoice as a branded PDF.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: InvoiceDetailPage,
  // The message comes with it: a page that only says it failed leaves the
  // operator with nothing to report and nobody able to tell a bad row from a
  // dropped request.
  errorComponent: ({ error }) => (
    <div className="space-y-2">
      <div className="text-destructive">Could not load this invoice.</div>
      <pre className="whitespace-pre-wrap rounded border bg-muted p-3 text-xs text-muted-foreground">
        {error instanceof Error ? (error.stack ?? error.message) : String(error)}
      </pre>
    </div>
  ),
  notFoundComponent: () => <div className="text-muted-foreground">Invoice not found.</div>,
});

type LineRow = {
  id: string;
  line_no: number;
  description: string;
  quantity: number;
  unit_amount: number;
  amount: number;
  source_type: string;
};

const STATUSES = ["draft", "sent", "partially_paid", "paid", "cancelled"] as const;

/**
 * The statuses a draft may be moved to by hand.
 *
 * Not `sent` or `paid`: those mean the invoice has been issued, which has to date
 * it and renumber it out of the client-facing series at the same time — the
 * "Issue invoice" action, not a status change. Cancelling a draft bills nothing
 * and needs neither.
 */
const DRAFT_STATUSES = ["draft", "cancelled"] as const;

/**
 * The statuses an issued invoice may be moved to by hand.
 *
 * `draft` is absent on purpose, and is the mirror of the rule above: the invoice
 * is already dated and already carries a number out of the client-facing series,
 * and neither can be taken back — the number has been handed out and the client
 * may be holding the document. Un-issuing it would leave a numbered, dated
 * invoice claiming never to have been sent, and would let the next issue hand
 * out a second number for the same charges. A mistaken invoice is `cancelled`,
 * which keeps the number spent and the trail intact.
 */
const ISSUED_STATUSES = ["sent", "partially_paid", "paid", "cancelled"] as const;

/**
 * Issues a draft: the dates to put on it, and the number it is about to take.
 *
 * Asked rather than assumed, because these are the facts the document is read by.
 * Both dates are required: an issued invoice is a document somebody is expected
 * to pay by a day, and neither the invoice date nor that day is something this
 * screen can infer — the terms are words on the invoice rather than a number of
 * days anything here can count. The invoice date is offered as today, the day it
 * is being issued; the due date is left empty so it is chosen rather than
 * accepted.
 */
function IssueInvoiceDialog({
  open,
  onOpenChange,
  number,
  onIssue,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  number: string;
  onIssue: (on: { invoice_date: string; due_date: string }) => Promise<void>;
}) {
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [issuing, setIssuing] = useState(false);

  // This component stays mounted while the dialog is shut — only the content
  // inside it unmounts — so the fields are re-seeded on each open rather than
  // once at page load. Without this, a dialog opened, half-filled and dismissed
  // comes back holding the abandoned dates, and "today" is whatever day the tab
  // was first opened on, which is wrong for a tab left open overnight.
  useEffect(() => {
    if (!open) return;
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    setDueDate("");
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // `required` on both inputs is what stops an empty submit; this is the same
    // rule stated where the value is read, so a browser that lets the form
    // through cannot issue an undated invoice.
    if (!invoiceDate || !dueDate) return;
    setIssuing(true);
    try {
      await onIssue({ invoice_date: invoiceDate, due_date: dueDate });
    } finally {
      setIssuing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={submit}
          className="space-y-4"
          // Enter in a field submits a form implicitly, and submitting this one
          // issues the invoice: it spends a number out of the client-facing
          // series, dates the document, and cannot be undone — an issued invoice
          // is cancelled, never returned to draft. That is a deliberate click on
          // the button, never a stray keypress while picking a date.
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Issue this invoice</DialogTitle>
            <DialogDescription>
              {number} is a draft. Issuing it dates it and gives it the next number in the client
              series — the draft number is not used again.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label required>Invoice date</Label>
              <Input
                type="date"
                required
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                The year of this date decides the number's year.
              </p>
            </div>
            <div className="space-y-2">
              <Label required>Due date</Label>
              <Input
                type="date"
                required
                min={invoiceDate || undefined}
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                The day payment is due — on or after the invoice date.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={issuing}>
              {issuing ? "Issuing…" : "Issue invoice"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceDetailPage() {
  const { invoiceId } = Route.useParams();
  const { canEdit } = useAuth();
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [draftLines, setDraftLines] = useState<LineRow[] | null>(null);
  // Declared up here with the rest of the state, not beside the issue handler
  // further down: everything below the `isLoading` guard is skipped on the first
  // render, so a hook down there is one more hook on the render after the
  // invoice arrives, which React rejects outright.
  const [issueOpen, setIssueOpen] = useState(false);

  const { data: invoice, isLoading } = useQuery({
    queryKey: ["invoice", invoiceId],
    queryFn: async () => {
      // A missing invoice reads as null, which the page renders as "not found"
      // rather than as a failure.
      return (await api.invoices.get(invoiceId)).data;
    },
  });
  const { data: lines = [] } = useQuery({
    queryKey: ["invoice_lines", invoiceId],
    queryFn: async () => {
      const { data, error } = await api.invoices.lines(invoiceId);
      if (error) throw error;
      return (data ?? []) as LineRow[];
    },
  });
  const { data: company } = useQuery({
    queryKey: ["invoice_company", invoice?.company_id],
    enabled: !!invoice?.company_id,
    queryFn: async () =>
      (
        await api.companies.get(invoice!.company_id!, [
          "id",
          "legal_name",
          "registered_office",
          "client_group_id",
        ])
      ).data,
  });
  // The invoice stamps the group it was raised under, but an invoice written
  // before that was stamped — or by a path that left it unset — still belongs
  // to the group its company is in, and is still billed through it.
  const groupId = invoice?.client_group_id ?? company?.client_group_id ?? null;
  const { data: group } = useQuery({
    queryKey: ["invoice_group", groupId],
    enabled: !!groupId,
    queryFn: async () =>
      (
        await api.clientGroups.get(groupId!, [
          "id",
          "name",
          // A group-billed invoice is addressed to the group, so it prints the
          // group's own address rather than the company's.
          ...POSTAL_ADDRESS_COLUMNS,
          "country",
        ])
      ).data,
  });
  const { data: bank } = useQuery({
    queryKey: ["invoice_bank", invoice?.bank_account_id],
    enabled: !!invoice?.bank_account_id,
    queryFn: async () => (await api.bankAccounts.get(invoice!.bank_account_id!)).data,
  });

  // The 59 leg and the instruction line, shared by every account. The archived
  // PDF the Lambda renders reads the same row, so the copy downloaded here and
  // the copy on file say the same thing.
  const { data: remittance } = useQuery({
    queryKey: ["remittance_defaults"],
    queryFn: async () => (await api.remittanceDefaults.list().all()).data?.[0] ?? null,
  });

  /** The account's own beneficiary address if it has one, else the shared default. */
  const beneficiaryAddress = bank?.beneficiary_address ?? remittance?.beneficiary_address ?? null;
  const { data: allNotes = [] } = useQuery({
    queryKey: ["invoice_notes_all"],
    // `.all()` because this is the lookup the printed note bodies are resolved
    // through, not a table: one page of it would silently drop the wording of
    // any note past the first page from the document.
    queryFn: async () => (await api.invoiceNotes.list().all()).data ?? [],
  });

  const rows = draftLines ?? lines;
  const total = useMemo(
    () => rows.reduce((s, l) => s + Number(l.quantity) * Number(l.unit_amount), 0),
    [rows],
  );
  const selectedNotes: string[] = ((invoice?.note_ids as string[] | null) ?? [])
    .map((id) => allNotes.find((n) => n.id === id)?.body)
    .filter(Boolean) as string[];

  if (isLoading) return <div className="text-muted-foreground">Loading…</div>;
  if (!invoice) return <div className="text-muted-foreground">Invoice not found.</div>;

  const patch = (id: string, p: Partial<LineRow>) =>
    setDraftLines((cur) => (cur ?? lines).map((l) => (l.id === id ? { ...l, ...p } : l)));

  // One call replaces the lines and re-totals the header, so the figure printed
  // on the invoice cannot end up disagreeing with the lines under it.
  const saveLines = async () => {
    if (!draftLines) return;
    setSaving(true);
    const { error } = await api.invoices.replaceLines(
      invoiceId,
      draftLines.map((l) => ({
        id: l.id,
        description: l.description,
        quantity: Number(l.quantity),
        unit_amount: Number(l.unit_amount),
      })),
    );
    setSaving(false);
    if (error) return notify.error(error);
    // Keep the archived copy in step with the edited lines.
    void requestInvoiceArchive(invoiceId).catch((archiveError) =>
      console.warn("Could not refresh the archived PDF", archiveError),
    );
    setDraftLines(null);
    notify.success("Invoice updated");
    qc.invalidateQueries({ queryKey: ["invoice_lines", invoiceId] });
    qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
    qc.invalidateQueries({ queryKey: ["invoices"] });
    invalidateInvoiceStamps(qc);
  };

  /**
   * Dates the draft and renumbers it out of the client-facing series.
   *
   * One request, because the three facts are one event: an invoice that had a
   * number but no date, or a date but still its draft number, is a document
   * nobody can file.
   */
  const issue = async (on: { invoice_date: string; due_date: string }) => {
    try {
      const issued = await issueInvoice(invoiceId, { ...on, status: "sent" });
      setIssueOpen(false);
      notify.success(`Issued as ${issued.invoice_number}`);
      // The archived PDF was of the draft, under the draft's number, so it is
      // dropped on issue and rendered again from the issued document.
      void requestInvoiceArchive(invoiceId).catch(() => {});
      qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      invalidateInvoiceStamps(qc);
    } catch (error) {
      notify.error(error);
    }
  };

  const setStatus = async (status: string) => {
    const { error } = await api.invoices.update(invoiceId, {
      status: status as (typeof STATUSES)[number],
    });
    if (error) return notify.error(error);
    notify.success(`Marked ${status.replace("_", " ")}`);
    qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
    qc.invalidateQueries({ queryKey: ["invoices"] });
    invalidateInvoiceStamps(qc);
  };

  const removeInvoice = async () => {
    if (
      !(await confirmDelete(
        `invoice ${invoice.invoice_number}`,
        "Any work and expenses on it become billable again.",
      ))
    ) {
      return;
    }
    // The release of the work and expenses it billed happens with the delete,
    // server-side: closing this tab part-way used to leave them stamped against
    // an invoice that no longer existed, and so never billable again.
    const { error } = await api.invoices.remove(invoiceId);
    if (error) return notify.error(error);
    notify.success("Invoice deleted");
    navigate({ to: "/invoices" });
  };

  /**
   * The copy on file in S3. The server draws it first when there is none, or
   * when an edit has made the one on file out of date.
   */
  const pdf = async () => {
    try {
      window.location.assign(await getInvoiceDownloadUrl(invoiceId));
    } catch (error) {
      notify.error(error);
    }
  };

  return (
    <div className="space-y-5 pb-8">
      <PageHeader
        title={invoice.invoice_number}
        description={`${company?.legal_name ?? group?.name ?? "—"}${invoice.period_label ? ` · ${invoice.period_label}` : ""}`}
        breadcrumbs={[{ label: "Invoices", to: "/invoices" }, { label: invoice.invoice_number }]}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/invoices">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Link>
            </Button>
            <Button variant="outline" onClick={() => void pdf()}>
              <FileDown className="mr-2 h-4 w-4" /> Download PDF
            </Button>
            {canEdit && draftLines && (
              <Button onClick={saveLines} disabled={saving}>
                <Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
              </Button>
            )}
            {canEdit && invoice.status === "draft" && (
              <Button onClick={() => setIssueOpen(true)}>
                <Send className="mr-2 h-4 w-4" /> Issue invoice
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-4">
        {/* A draft has no invoice date — it is dated when it is issued — so the
            fact shown is the one that exists: the day the draft was started. */}
        {invoice.invoice_date ? (
          <Info label="Invoice date" value={invoice.invoice_date} />
        ) : (
          <Info label="Drafted" value={String(invoice.created_at ?? "").slice(0, 10)} />
        )}
        <Info label="Due date" value={invoice.due_date ?? "—"} />
        <Info label="Total" value={money(total)} />
        <div className="rounded-lg border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Status</div>
          {canEdit ? (
            <Select value={invoice.status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(invoice.status === "draft" ? DRAFT_STATUSES : ISSUED_STATUSES).map((s) => (
                  <SelectItem key={s} value={s}>
                    {s.replace("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Badge className="mt-2">{invoice.status}</Badge>
          )}
          {invoice.status === "draft" && (
            <p className="mt-1.5 text-xs text-muted-foreground">
              Undated and unnumbered until issued.
            </p>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lines</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>
                  Description
                  {canEdit && (
                    <span aria-hidden="true" className="ml-0.5 text-destructive">
                      *
                    </span>
                  )}
                </TableHead>
                <TableHead className="w-24 text-right">Qty</TableHead>
                <TableHead className="w-32 text-right">Rate</TableHead>
                <TableHead className="w-32 text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((l, i) => (
                <TableRow key={l.id}>
                  <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                  <TableCell>
                    {canEdit ? (
                      <Input
                        value={l.description}
                        onChange={(e) => patch(l.id, { description: e.target.value })}
                      />
                    ) : (
                      l.description
                    )}
                    <Badge variant="outline" className="mt-1 text-[10px]">
                      {l.source_type.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {canEdit ? (
                      <Input
                        className="text-right"
                        type="number"
                        step="any"
                        value={l.quantity}
                        onChange={(e) => patch(l.id, { quantity: Number(e.target.value) || 0 })}
                      />
                    ) : (
                      l.quantity
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {canEdit ? (
                      <Input
                        className="text-right"
                        type="number"
                        step="any"
                        value={l.unit_amount}
                        onChange={(e) => patch(l.id, { unit_amount: Number(e.target.value) || 0 })}
                      />
                    ) : (
                      money(Number(l.unit_amount))
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {money(Number(l.quantity) * Number(l.unit_amount))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="mt-4 flex justify-end text-lg font-semibold">Total: {money(total)}</div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Payment instructions</CardTitle>
            <CardDescription>{bank ? bank.bank_name : "No bank account selected"}</CardDescription>
          </CardHeader>
          {bank && (
            <CardContent className="space-y-1 text-sm text-muted-foreground">
              <div>
                <span className="text-foreground">Account name:</span> {bank.account_name}
              </div>
              {beneficiaryAddress && (
                <div>
                  <span className="text-foreground">Beneficiary address:</span> {beneficiaryAddress}
                </div>
              )}
              {bank.account_number && (
                <div>
                  <span className="text-foreground">Account no.:</span> {bank.account_number}
                </div>
              )}
              {bank.iban && (
                <div>
                  <span className="text-foreground">IBAN:</span> {bank.iban}
                </div>
              )}
              {bank.swift_code && (
                <div>
                  <span className="text-foreground">SWIFT:</span> {bank.swift_code}
                </div>
              )}
              {bank.intermediary_bank && (
                <div>
                  <span className="text-foreground">Intermediary:</span> {bank.intermediary_bank}
                  {bank.intermediary_bank_address ? `, ${bank.intermediary_bank_address}` : ""}
                </div>
              )}
            </CardContent>
          )}
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm text-muted-foreground">
            {selectedNotes.length === 0 && !invoice.notes && <p>No notes on this invoice.</p>}
            {/* The bodies go to the PDF as they are stored — markup and all,
                which is what the renderer reads. On screen they are summarised as
                words; the invoice itself is where the formatting is drawn. */}
            {selectedNotes.map((n, i) => (
              <div key={i} className="whitespace-pre-line">
                {i + 1}. {richTextToPlain(n)}
              </div>
            ))}
            {invoice.notes && (
              <p className="whitespace-pre-line pt-2 italic">{richTextToPlain(invoice.notes)}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {canEdit && (
        <div className="flex justify-end">
          <Button variant="ghost" className="text-destructive" onClick={removeInvoice}>
            <Trash2 className="mr-2 h-4 w-4" /> Delete invoice
          </Button>
        </div>
      )}

      <IssueInvoiceDialog
        open={issueOpen}
        onOpenChange={setIssueOpen}
        number={invoice.invoice_number}
        onIssue={issue}
      />
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-medium">{value}</div>
    </div>
  );
}
